import {
  BYPASS_PERMISSION,
  type ClientAccessRequirement,
  EMPTY_REQUIREMENT_LOG,
  UNASSIGNED_CLIENTS_FULL_KEY,
  UNASSIGNED_CLIENTS_LIMITED_KEY,
} from '@kg-internal/pv3-helpers';

/*
 * Full - Full access to the client including write access
 * Limited - Limited access to the client: read-only access
 */
export type clientPermissionLevel = 'full' | 'limited';

/**
 * THE BROWSER'S TWO PERMISSION DECISIONS, as pure functions over what the `me` query returned.
 *
 * Split out of userObj.ts, which imports the app config and therefore `window`, so these can be tested
 * from server/graph — packages/uikit has no test runner of any kind, and the closest one whose tsconfig
 * has no DOM lib is the graph's. Same reason hiring's `testTypeScope.ts` stands alone, and the same
 * trade: a module with no environment dependency is the one that can be pinned.
 *
 * They take the held permissions rather than the user object for the same reason: nothing here should be
 * able to reach a second source of truth about what somebody holds.
 *
 * WHAT THEY ARE HANDED IS `me.permissionKeys` — the NEW vocabulary, in both flag positions. It used to be
 * `me.permissions`, a list of legacy NAMES, and that is the field this change exists to stop reading:
 * the browser cannot be converted one call site at a time while the set underneath it still speaks the
 * other language. The server answers the same question either side of the flag (`Person.permissionKeys`),
 * so nothing here has to know which system is enforced.
 *
 * `readonly string[]` rather than the key union, because this is what the query returned: a key the code
 * does not declare has already been dropped server-side, and re-narrowing here would only move the same
 * check to a place with less information.
 */

/** `permissionType` is the service-team row's free VARCHAR, as the server reads it in `rowLevel`. */
type ServiceTeamRow = { permissionType: string; projectAccountID: number };

/**
 * WHETHER THE BROWSER HONOURS THE PLATFORM BYPASS. This is the WHOLE switch: every browser permission
 * decision — `usePermissions`, `<Permission name=…>`, `userObj.hasPermissions`, orion's nav items, the
 * portal nav map, the two client-access questions — resolves through `holdsKey` below, and this constant
 * is the only thing it consults about `portal.superadmin`. Flip it here and the whole UI moves together.
 *
 * IT IS OFF, and the two positions are a genuine product choice rather than a correctness question:
 *
 *   OFF (today)  a superadmin sees the chrome their own grants earn them, and nothing more. The server
 *                guards DO bypass, so a typed URL still works — the UI just does not advertise it.
 *   ON           a superadmin sees every button, link and column in the portal, matching what the
 *                server would let them do. Nothing becomes reachable that was not already reachable.
 *
 * Either way the server is the authority, so this cannot grant anything: it decides what is DRAWN.
 * `withoutBypass` guards are the one thing it must not be allowed to contradict — but those refuse the
 * bypass server-side by name and there is no browser twin of them, so flipping this cannot reach one.
 *
 * ANNOTATED `: boolean` ON PURPOSE, which is the opposite of the rule `BYPASS_PERMISSION` follows.
 * There the literal type is load-bearing; here widening is, because a `false` literal makes the
 * bypass branch below unreachable code that a linter or a reader may then "clean up", and flipping the
 * switch would silently do nothing.
 */
export const BROWSER_HONOURS_SUPERADMIN_BYPASS: boolean = false;

/**
 * The ONE key-membership test on the browser side. Everything above goes through it, and
 * `portalNavUtils.ts` imports it rather than testing its own Set, so the switch has exactly one reader.
 */
export const holdsKey = (held: readonly string[], key: string): boolean => {
  if (BROWSER_HONOURS_SUPERADMIN_BYPASS && held.includes(BYPASS_PERMISSION)) {
    return true;
  }

  return held.includes(key);
};


/**
 * AN EMPTY REQUIREMENT REFUSES, on both the list and the string path — the browser half of the rule the
 * two server implementations already carry.
 *
 * It used to RETURN TRUE, so a component whose permission prop defaulted to `[]` rendered to every
 * employee. `ClientPagePermission` was exactly that on 43 Polaris surfaces. The absence of an ARM is a
 * different statement and has its own spelling (`NO_PERMISSION_ARM`); this is the caller bug.
 *
 * Logged rather than thrown, for the reason `EMPTY_REQUIREMENT_LOG` gives: these run per render.
 *
 * THE PLATFORM BYPASS IS OFF HERE BY CONFIGURATION, not by omission — see
 * `BROWSER_HONOURS_SUPERADMIN_BYPASS`, which this reaches through `holdsKey` and which is the only
 * place the browser decides the question.
 */
export const hasPermissions = (
  held: readonly string[],
  permissionKeys: string | readonly string[],
  allRequired = false,
): boolean => {
  // `typeof` rather than `Array.isArray`, which narrows a `readonly string[]` union to `unknown[]` under
  // some of this monorepo's tsconfigs and compiles in one app while failing in another.
  if (typeof permissionKeys === 'string') {
    if (permissionKeys === '') {
      console.error(EMPTY_REQUIREMENT_LOG, { allRequired });

      return false;
    }

    return holdsKey(held, permissionKeys);
  }

  if (permissionKeys.length === 0) {
    console.error(EMPTY_REQUIREMENT_LOG, { allRequired });

    return false;
  }

  return allRequired
    ? permissionKeys.every((key) => holdsKey(held, key))
    : permissionKeys.some((key) => holdsKey(held, key));
};

/**
 * THE TWO CLIENT-ACCESS QUESTIONS, one function each, mirroring `clientPermissionGate.ts` name for name.
 *
 * They replace `hasClientPermissions(accountId, permissionLevels?)`. The array is gone, and with it the
 * hole it carried: OMITTING the levels admitted every employee on any real account, at eleven call sites
 * — six Polaris `serviceMap` route guards, two rate-card cards, the engagement actions dropdown and the
 * Commander company command — every one of which read `const onCST = user.hasClientPermissions(id)` and
 * had therefore always been TRUE. There is no argument left to omit, so the hole is not guarded against;
 * it cannot be written.
 *
 * `hasAtLeastLimitedClientAccess` is named for the question and not for the value. A function named for
 * `limited` that matched `=== 'limited'` would refuse a caller holding `full`, which is never what any of
 * these sites means.
 */

/**
 * THE ACCOUNT AXIS, and the browser's copy of `granularClientAccessLevel` — branch for branch, in the
 * same order, with the same three return values (`clientPermissionGate.ts`).
 *
 * It used to read three legacy NAMES: `crm.admin` and `portal.superadmin` for `full`, `crm.srmanager`
 * for `limited`. `legacy_permission_keys` maps exactly those three onto these two keys and nothing else
 * onto either, so at flag-off this admits precisely who it admitted before; at flag-on it is what the
 * server itself decides. That is what makes this change cover the client decisions that name no
 * permission at all — they take an account id and go through here.
 *
 * TWO BRANCHES OF THE OLD BODY ARE GONE and neither took anybody with it:
 *
 *   * The early `!accountId` return. `ProjectAccountID` is auto-increment from 1, so no row can match a
 *     falsy id and the no-row branch below already gives the same answer.
 *   * Returning the row's own level unconditionally. The server has raised an assigned client to `full`
 *     for a company-wide `limited` holder since before the flag existed and the browser never did —
 *     measured inert either way, because all 1,523 `accountserviceteam` rows are `full`, so there is no
 *     `limited` row for it to raise.
 */
const clientAccessLevel = (
  held: readonly string[],
  serviceTeams: readonly ServiceTeamRow[],
  accountId: number,
): clientPermissionLevel | null => {
  if (holdsKey(held, UNASSIGNED_CLIENTS_FULL_KEY)) {
    return 'full';
  }

  const assigned = holdsKey(held, UNASSIGNED_CLIENTS_LIMITED_KEY);
  const row = serviceTeams.find((team) => team.projectAccountID === accountId);

  if (row) {
    // Assignment raises the level, so a `limited` row on a client this person is on reads as `full`.
    if (assigned) {
      return 'full';
    }

    // Only the two levels count; `'none'` or any other value grants nothing, as `rowLevel` on the server.
    return row.permissionType === 'full' || row.permissionType === 'limited' ? row.permissionType : null;
  }

  return assigned ? 'limited' : null;
};

/** `full` on this account — write access. Replaces `permissionLevels: ['full']` and its 122 call sites. */
export const hasFullClientAccess = (
  held: readonly string[],
  serviceTeams: readonly ServiceTeamRow[],
  accountId: number,
): boolean => clientAccessLevel(held, serviceTeams, accountId) === 'full';

/** `full` OR `limited` — any reach at all. Replaces `permissionLevels: ['full', 'limited']`. */
export const hasAtLeastLimitedClientAccess = (
  held: readonly string[],
  serviceTeams: readonly ServiceTeamRow[],
  accountId: number,
): boolean => clientAccessLevel(held, serviceTeams, accountId) !== null;

/** Dispatches on a requirement carried as a value — what a COMPONENT PROP has instead of two functions. */
export const meetsClientAccessRequirement = (
  held: readonly string[],
  serviceTeams: readonly ServiceTeamRow[],
  accountId: number,
  required: ClientAccessRequirement,
): boolean =>
  required === 'full'
    ? hasFullClientAccess(held, serviceTeams, accountId)
    : hasAtLeastLimitedClientAccess(held, serviceTeams, accountId);
